import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ReportClass } from '@modules/reporting/models';
import { DeploymentService } from '@modules/management/pages/details/check/services';
import { AssetSummaryModelService } from '@modules/reporting/models/types/asset/service';
import { ObservationDetailService } from '@services/observationDetail/observation-detail.service';
import { ReportingFormBuilderService } from '@modules/reporting/services/reporting-form-builder.service';
import {
  AssetsService,
  ChecksService,
  ObservationService,
  PermissionsService,
  ShiftService,
  TeamsService,
  UserService,
  UtilsService,
  ZoneService
} from '@services';

import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { each, filter, find, get, includes, isArray, isString, map } from 'lodash';

// CVX-6347 / CVX-6349: the Check: Detail catalog ids matching the columns the
// drill-down builds for itself in getCheckColumns() when a report configures
// none. Used to seed the "Drill-down Columns" picker so opening the edit form
// shows what the report already displays -- otherwise selecting one column
// would silently drop the other six.
//
// This mirrors the legacy block at the foot of getCheckColumns(); the two are
// kept in step by hand. Unifying them means re-rendering every existing
// report through the catalog's formatters, which is a separate change.
const LEGACY_DRILLDOWN_COLUMNS = [
  'checkDetailName', 'deploymentName', 'group', 'responder', 'target', 'Status', 'checkResult'
];

export function defaultDrilldownColumns(checkStatus: any): string[] {
  const statuses = isArray(checkStatus) ? checkStatus : [checkStatus];
  const dates = [];

  // getCheckColumns() prepends these, newest guard first: available, complete,
  // missed -- so build them in that order.
  if (includes(statuses, 'available')) {
    dates.push('availableTime');
  }
  if (includes(statuses, 'complete') || includes(statuses, 'groupsCompleted') || includes(statuses, 'skipped')) {
    dates.push('completionTime');
  }
  if (includes(statuses, 'missed')) {
    dates.push('expiresTime');
  }

  return [...dates, ...LEGACY_DRILLDOWN_COLUMNS];
}

@Injectable({
  providedIn: 'root'
})
export class DetailReportTableService {
  private queryParams: any = {};
  private tables: any = {
    [ReportClass.Observation]: {
      class: 'detailReport',
      rowprop: 'data-mid',
      rowvaluefrom: 'observationID',
      columns: [
        {
          id: 'observationID',
          title: this.translate.instant('SHARED.Response_ID'),
          headerClass: 'table-header'
        },
        {
          id: 'type',
          title: this.translate.instant('SHARED.TYPE'),
          headerClass: 'table-header',
          func: (type: string, observation: any) => this.translate.instant(this.observationService.typeMap[this.observationService.getProperty(observation, 'type')])
        },
        {
          id: 'subtype',
          title: this.translate.instant('SHARED.SUBTYPE'),
          headerClass: 'table-header',
          func: (type: string, observation: any) => {
            const f = this.observationService.getProperty(observation, 'subtype');
            if (this.observationService.subtypeMap[f]) {
              return this.observationService.subtypeMap[f];
            } else {
              return f;
            }
          }
        },
        {
          id: 'userID',
          title: this.translate.instant('REPORTING.CREATOR'),
          headerClass: 'table-header',
          func: (userID: number) => this.userService.getFullname(userID)
        },
        {
          id: 'created',
          title: this.translate.instant('REPORTING.CREATED'),
          func: (time) => moment(time * 1000).fromNow(),
          headerClass: 'table-header'
        },
        {
          id: 'ownerID',
          title: this.translate.instant('SHARED.OWNER'),
          func: (ownerID: number) => this.userService.getFullname(ownerID),
          headerClass: 'table-header'
        },
        {
          id: 'groupID',
          title: this.translate.instant('SHARED.TEAM'),
          func: (groupID: number) => this.teamsService.teamNameByID(groupID),
          headerClass: 'table-header'
        }
      ],
      onClickRow: (messageID: string): any => {
        const routeParams: any = {
          reportId: this.queryParams.reportId,
          headerIsHidden: true,
          prevState: this.queryParams.prevState
        };
        this.observationDetailService.navigateToDetail(this.observationService.observations.data[messageID], [], false, false, routeParams);
      }
    },
    [ReportClass.Worker]: {
      class: 'detailReport',
      rowprop: 'data-mid',
      rowvaluefrom: 'userID',
      columns: [
        {
          id: 'userID',
          title: 'User Id',
          headerClass: 'table-header'
        },
        {
          id: 'type',
          title: this.translate.instant('SHARED.TYPE'),
          headerClass: 'table-header no-vis',
          func: (val) => {
            if (val === undefined || val === null || val === '' || val === 'individual' || val === 'dedicated') {
              return 'Dedicated';
            } else if (val === 'shared') {
              return 'Shared';
            } else if (val === 'observation') {
              return 'Observation';
            } else if (val === 'reporting') {
              return 'Reporting';
            } else if (val === 'coordinator') {
              return 'Coordinator';
            } else if (val === 'wristband') {
              return 'Wristband-Only';
            } else if (val === 'observer') {
              return 'Observer';
            } else if (val === 'viewer') {
              return 'Viewer';
            }
          }
        },
        {
          id: 'username',
          title: this.translate.instant('MGMT_LIST.USERNAME'),
          headerClass: 'table-header'
        },
        {
          id: 'lastname',
          title: this.translate.instant('MGMT_LIST.LAST_NAME'),
          headerClass: 'table-header'
        },
        {
          id: 'firstname',
          title: this.translate.instant('MGMT_LIST.FIRST_NAME'),
          headerClass: 'table-header'
        },
        {
          id: 'email',
          title: this.translate.instant('MGMT_LIST.EMAIL'),
          headerClass: 'table-header'
        },
        {
          id: 'permissions',
          title: this.translate.instant('MGMT_LIST.PERMISSIONS'),
          headerClass: 'table-header',
          fromID: 'permissions',
          func: (permissions) => {
            let Tstring = '';
            if (permissions) {
              each(permissions, (val, key) => {
                const f: any = find(this.permissionsService.permissions.data, {id: key});
                if (f) {
                  Tstring += this.translate.instant(f.description) + '<br />';
                }
              });
            }
            return Tstring;
          }
        },
        {
          id: 'locations',
          title: this.translate.instant('MGMT_LIST.LOCATIONS'),
          headerClass: 'table-header',
          func: (val) => {
            if (!val.length) {
              return this.translate.instant('SHARED.All_Locations');
            } else {
              let r = '';
              each(val, (loc) => {
                const l = this.userService.findLocation(loc);
                if (l) {
                  if (r !== '') {
                    r += ', ';
                  }
                  r += l.name;
                }
              });
              return r;
            }
          }
        },
        {
          id: 'team',
          title: this.translate.instant('SHARED.TEAM'),
          headerClass: 'table-header',
          fromID: 'groups',
          func: (groups, row) => {
            let Tstring = '';
            if (groups) {
              groups.forEach((val) => {
                if (row.primaryGroup && val == row.primaryGroup) {
                  Tstring += '*&nbsp;';
                }
                Tstring += this.teamsService.teamNameByID(val) + '</br>';
              });
            }
            return Tstring;
          }
        },
        {
          id: 'shift',
          title: this.translate.instant('MGMT_LIST.SHIFT'),
          headerClass: 'table-header',
          func: (shift, row) => this.shift.name(shift)
        },
        {
          id: 'lastZone',
          title: this.translate.instant('MGMT_LIST.LAST_ZONE'),
          headerClass: 'table-header',
          func: (zoneID, row) => {
            let ret = 'N/A';
            if (row.lastLocation) {
              const location = this.userService.findLocation(row.lastLocation);
              if (location) {
                if (zoneID === 0) {
                  ret = location.name + '/Site-Wide';
                } else {
                  const zone = this.zoneService.findZoneRecursive(location.zones, zoneID);
                  if (zone) {
                    ret = location.name + '/' + zone.name;
                  }
                }
              }
            }
            return ret;
          }
        }
      ]
    },
    [ReportClass.Check]: {
      class: 'detailReport',
      rowprop: 'data-mid',
      rowvaluefrom: 'responseID',
      columns: this.getCheckColumns()
    },
    [ReportClass.PPESummary]: {
      class: 'detailReport',
      rowprop: 'data-mid',
      rowvaluefrom: 'gearID',
      columns: [
        {
          id: 'name',
          title: this.translate.instant('SHARED.Name'),
          headerClass: 'table-header',
          func: (name, row) => name
        },
        {
          id: 'description',
          title: this.translate.instant('SHARED.Description'),
          headerClass: 'table-header',
          func: (description, row) => description
        }
      ]
    },
    [ReportClass.AssetSummary]: {
      class: 'detailReport',
      rowprop: 'data-mid',
      rowvaluefrom: 'assetID',
      columns: []
    }
  };

  constructor(
    private translate: TranslateService,
    private observationService: ObservationService,
    private userService: UserService,
    private zoneService: ZoneService,
    private teamsService: TeamsService,
    private observationDetailService: ObservationDetailService,
    private permissionsService: PermissionsService,
    private shift: ShiftService,
    private checksService: ChecksService,
    private deployment: DeploymentService,
    private assetsService: AssetsService,
    private utils: UtilsService,
    private reportingFormBuilderService: ReportingFormBuilderService,
    private assetSummaryModelService: AssetSummaryModelService,
    private router: Router,
    protected route: ActivatedRoute
  ) {}

  // CVX-6347 / CVX-6349: resolve the drill-down columns a Check: Summary report
  // was configured with against the Check: Detail catalog. Returns [] when the
  // report has none configured, which keeps the legacy status-derived columns.
  //
  // The catalog's funcs take a collection -- func([response], type) -- while
  // this table calls func(row[id], row) with no orthogonal type argument, so
  // each one is wrapped. That also means the drill-down renders display text
  // only: it has no 'sort' or 'export' hook to forward.
  private getDrilldownColumns(selected: any, catalog?: any[]): any[] {
    if (!catalog || !selected) {
      return [];
    }
    const ids = isArray(selected) ? selected : [selected];

    return map(
      filter(map(ids, (id) => find(catalog, { id }))),
      (def: any) => ({
        id: def.id,
        title: def.label || def.description,
        headerClass: 'table-header',
        nowrap: def.nowrap,
        func: (value, row) => def.func([row])
      })
    );
  }

  // CVX-6347 / CVX-6349: drilldownCatalog is the Check: Detail column catalog,
  // supplied by the caller. It is NOT injected here -- CheckDetailModelService
  // takes this service in its constructor, so importing it back would put
  // DetailReportTableService into that class's decorator metadata before this
  // module finished evaluating, and Angular would fail DI on a circular token.
  public getTableConfigByType(type: ReportClass, drilldownCatalog?: any[]) {
    const table = this.tables[type] || {};

    if (type === ReportClass.Check) {
      table.columns = this.getCheckColumns(drilldownCatalog);
      // we need a row click handler for check reports
      table.onClickRow = (responseID: string): any => {
        this.router.navigate([`pages/dashboard/check-detail/${responseID}`], {queryParams: {skipFooter: true}});
      };
    } else if (type === ReportClass.AssetSummary) {
      table.columns = this.assetSummaryModelService.getAssetColumns(this.queryParams);
    }

    return table;
  }

  public applyRouteParams(queryParams: any): void {
    const reportType: ReportClass = get(queryParams, 'reportType');

    if (reportType === ReportClass.Observation) {
      const fieldKeys: string[] = [
        'behaviors',
        'locations',
        'zones',
        'groups',
        'categories',
        'recipients',
        'targetGroups',
        'mitigations',
        'qualitycats',
        'compliments',
        'users',
        'owners',
        'creators',
        'tags',
        'picats'
      ];
      this.queryParams = this.parseRouteParams(queryParams, fieldKeys);
    } else if (includes([ReportClass.Check, ReportClass.AssetSummary], reportType)) {
      this.queryParams = this.parseRouteParams(queryParams, []);
      this.parseQueryParamsFromJSON();
    } else {
      this.queryParams = queryParams;
    }
  }

  public getTableDataByType(type: ReportClass) {
    const reportInstance = this.reportingFormBuilderService.getReportInstanceByType(type);
    return reportInstance ? reportInstance.getTableItems(this.queryParams) : null;
  }

  public getCheckColumns(drilldownCatalog?: any[]) {
    const queryParams = this.route.snapshot.queryParams;
    const configured = this.getDrilldownColumns(queryParams.drilldownColumns, drilldownCatalog);
    if (configured.length) {
      return configured;
    }
    let checkStatus = queryParams.checkStatus;
    let isAvailable = false;
    let isComplete = false;
    let isMissed = false;
    let statusColumns = [];
    let columns = [
      {
        id: 'checkID',
        title: this.translate.instant('SHARED.Check_Title'),
        headerClass: 'table-header',
        func: (checkID, row) => {
          const check = this.checksService.getCheck(checkID);
          let title = checkID;
          if (check && check.title) {
            title = check.title;
          }
          return title;
        }
      },
      {
        id: 'deploymentID',
        title: this.translate.instant('SHARED.Deployment_Title'),
        headerClass: 'table-header',
        func: (deploymentID, row) => {
          const deployment = this.deployment.getDeployment(deploymentID);
          let label: any = 'None';
          if (deployment) {
            label = deployment.title;
          }
          return label;
        }
      },
      {
        id: 'checkGroup',
        title: this.translate.instant('SHARED.Check_Group_Name'),
        headerClass: 'table-header',
        func: (val, ref) => {
          const group = find(ref?.check?.checkGroups, { groupID: ref?.checkGroup });
          return group?.title || this.translate.instant('SHARED.None');
        }
      },
      {
        id: 'owner',
        title: this.translate.instant('SHARED.Responder'),
        headerClass: 'table-header',
        func: (owner, row) => this.userService.getFullname(owner)
      },
      {
        id: 'target',
        title: 'Target',
        headerClass: 'table-header',
        func: (target, row) => {
          // Use the response's targetSignature to get the correct target
          const targetSig = row.targetSignature;

          if (!targetSig) {
            // Map target type to translated name
            const targetTitleMap = {
              assets: this.translate.instant('SHARED.ASSETS'),
              zones: this.translate.instant('SHARED.Zones'),
              workers: this.translate.instant('SHARED.Workers')
            };
            return targetTitleMap[target.targetType] || target.targetType;
          }

          const parts = targetSig.split(':');
          const targetType = parts[0];  // 'asset', 'loc', 'worker', etc.
          const targetId = parts[1];     // the ID

          if (targetType === 'asset') {
            const asset = this.assetsService.getAssetById(+targetId);
            if (asset && asset.length) {
              return asset[0].name;
            }
          } else if (targetType === 'loc' && parts.length >= 3) {
            const locId = +targetId;
            const zoneId = parts[2] ? +parts[2] : null;
            const loc = this.userService.findLocation(locId);
            if (loc && loc.zones && zoneId) {
              const zone = this.userService.getZoneById(loc.zones, zoneId);
              if (zone) {
                return zone.name;
              }
            }
          } else if (targetType === 'worker') {
            return this.userService.getFullname(+targetId);
          }

          return targetSig;  // fallback to showing the signature itself
        }
      },
      {
        id: 'status',
        title: this.translate.instant('SHARED.Status'),
        headerClass: 'table-header',
        func: (val, ref) => {
          let label = val;
          if (val === 'complete') {
            label = this.translate.instant('SHARED.Completed');
          } else if (val === 'groupsCompleted') {
            label = this.translate.instant('SHARED.OptionalGroups');
          } else if (val === 'available') {
            label = this.translate.instant('SHARED.Available');
          } else if (val === 'missed') {
            label = this.translate.instant('SHARED.Missed');
          } else if (val === 'skipped') {
            label = this.translate.instant('SHARED.Skipped');
          } else if (val === 'notCompleted') {
            label = this.translate.instant('SHARED.NotCompleted');
          }
          return label;
        }
      },
      {
        id: 'result',
        title: this.translate.instant('SHARED.Check_Result'),
        headerClass: 'table-header',
        func: (val, ref) => {
          let title = val;
          if (val === 'negative') {
            title = this.translate.instant('SHARED.Negative');
          } else if (val === 'neutral') {
            title = this.translate.instant('SHARED.Neutral');
          } else if (val === 'positive') {
            title = this.translate.instant('SHARED.Positive');
          } else if (val === 'unknown') {
            title = this.translate.instant('SHARED.None');
          }
          return title;
        }
      }
    ];
    if (!isArray(checkStatus)) {
      checkStatus = [checkStatus];
    }
    each(checkStatus, (status) => {
      if (status === 'available') {
        isAvailable = true;
      } else if (status === 'complete' || status === 'groupsCompleted' || status === 'skipped') {
        isComplete = true;
      } else if (status === 'missed') {
        isMissed = true;
      }
    });
    if (isMissed) {
      const missed = [{
        id: 'expiresTime',
        title: this.translate.instant('SHARED.Expires_Time'),
        headerClass: 'table-header',
        func: (expiresTime, row) => this.utils.dateTimeFormat(expiresTime, null, true)
      }];
      statusColumns = [
        ...missed,
        ...statusColumns
      ];
    }
    if (isComplete) {
      const complete = [{
        id: 'completionTime',
        title: this.translate.instant('SHARED.Completion_Time'),
        headerClass: 'table-header',
        func: (completionTime, row) => this.utils.dateTimeFormat(completionTime, null, true)
      }];
      statusColumns = [
        ...complete,
        ...statusColumns
      ];
    }
    if (isAvailable) {
      const available = [{
        id: 'availableTime',
        title: this.translate.instant('SHARED.Available_Time'),
        headerClass: 'table-header',
        func: (availableTime, row) => this.utils.dateTimeFormat(availableTime, null, true)
      }];
      statusColumns = [
        ...available,
        ...statusColumns
      ];
    }
    columns = [
      ...statusColumns,
      ...columns
    ];
    return columns;
  }

  private parseRouteParams(queryParams, fieldKeys): any {
    queryParams.endTime = +queryParams.endTime;
    queryParams.hasWorkorder = +queryParams.hasWorkorder;
    queryParams.startTime = +queryParams.startTime;

    each(fieldKeys, (key: string) => {
      if (isArray(queryParams[key])) {
        queryParams[key] = map(queryParams[key], Number);
      }
    });

    if (queryParams?.zones?.length) {
      queryParams.zones = map(queryParams.zones, String);
    }

    return queryParams;
  }

  public getExcelFormatDate(unixTime: number) {
    const date = this.utils.dateTimeFormat(unixTime);
    const dateMoment = moment(unixTime * 1000);
    return dateMoment.isValid() ? dateMoment.toDate() : date;
  }

  private parseQueryParamsFromJSON() {
    each(this.queryParams, (param, index) => {
      if (isString(param)) {
        try {
          this.queryParams[index] = JSON.parse(param);
        } catch (e) {}
      }
    });
  }
}
